import React, { useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  Heading,
  IconButton,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowLeft, CaretRight, Gauge, Warning } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';

import { GlassCard, GlassPageBg } from '../../../../../components/glass';
import {
  useContaCilDashboard,
  useContaCilOperacional,
  useContaCilResumo,
} from '../../../../../hooks/useContaCilDashboard';
import { useContaCilEmpresasSaude } from '../../../../../hooks/useContaCilEmpresas';
import { useContaCilImports } from '../../../../../hooks/useContaCilImportar';
import { EmpresasPanel } from '../components/EmpresasPanel';
import { EmpresasTabela } from '../components/EmpresasTabela';
import { FechamentosCalendar } from '../components/FechamentosCalendar';
import { FunilProcessamento } from '../components/FunilProcessamento';
import { HomologacaoPdf } from '../components/HomologacaoPdf';
import { KPICards } from '../components/KPICards';
import { OperacionalKpis } from '../components/OperacionalKpis';
import { VolumeCalendar } from '../components/VolumeCalendar';
import { YearDetailPanel } from '../components/YearDetailPanel';
import { useVoltar } from '../useVoltar';

const CURRENT_YEAR = new Date().getFullYear();
const ANOS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR + 1 - i);
const CONCILIAR_PATH = '/modulesHome/accountingConciliation/conciliacao-contabil/conciliar';
const EMPRESAS_PATH = '/modulesHome/accountingConciliation/conciliacao-contabil/empresas';
const EXTRATOS_PATH = '/modulesHome/accountingConciliation/conciliacao-contabil/extratos';

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

const formatBRL = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Text
    fontSize="11px"
    fontWeight="600"
    color="#94A3B8"
    textTransform="uppercase"
    letterSpacing="0.08em"
    mb={3}
  >
    {children}
  </Text>
);

/** Cockpit ContaCil — visão geral (BI) + operacional, sem poluir o hub. */
export function ConciliacaoContabilDashboardPage() {
  const voltar = useVoltar();
  const navigate = useNavigate();
  const [ano, setAno] = useState<number>(CURRENT_YEAR);

  const { data, isLoading } = useContaCilDashboard({ ano });
  const { data: operacional, isLoading: operLoading } = useContaCilOperacional(ano);
  const { data: resumo } = useContaCilResumo(ano);
  const saude = useContaCilEmpresasSaude();
  const aIdentificar = useContaCilImports('a_identificar');

  const meses = data?.meses_status ?? [];
  const kpis = data?.kpis;

  const empresasCriticas = useMemo(
    () => (saude.data?.itens ?? []).filter(e => e.pendentes > 0).slice(0, 10),
    [saude.data],
  );
  const nAIdentificar = aIdentificar.data?.length ?? 0;

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
        >
          <Flex align="center" gap={3} mb={6} wrap="wrap">
            <IconButton
              aria-label="Voltar"
              icon={<ArrowLeft size={18} weight="bold" />}
              variant="ghost"
              size="sm"
              borderRadius="full"
              color="#64748B"
              _hover={{ bg: 'rgba(15, 23, 42, 0.06)', color: '#0F172A' }}
              onClick={voltar}
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="64px"
              h="64px"
              borderRadius="2xl"
              bgGradient="linear(135deg, #EDE9FE 0%, #DDD6FE 100%)"
              color="#7C3AED"
              boxShadow="0 8px 24px -6px rgba(124, 58, 237, 0.4)"
              flexShrink={0}
            >
              <Gauge size={32} weight="duotone" />
            </Box>
            <Box>
              <Text
                fontSize="11px"
                color="#94A3B8"
                textTransform="uppercase"
                letterSpacing="0.1em"
                fontWeight="semibold"
              >
                Conciliação Contábil
              </Text>
              <Heading fontSize={{ base: '22px', md: '28px' }} fontWeight="700" color="#0F172A">
                Dashboard
              </Heading>
            </Box>
            <Box flex={1} />
            <Select
              value={ano}
              onChange={e => setAno(Number(e.target.value))}
              w="120px"
              size="sm"
              borderRadius="lg"
              bg="white"
            >
              {ANOS.map(a => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </Flex>
        </motion.div>

        <Tabs variant="soft-rounded" colorScheme="purple">
          <TabList mb={6} gap={2}>
            <Tab fontSize="sm" fontWeight="600">
              Visão geral
            </Tab>
            <Tab fontSize="sm" fontWeight="600">
              Operacional
            </Tab>
          </TabList>

          <TabPanels>
            {/* ---------- VISÃO GERAL (BI) ---------- */}
            <TabPanel p={0}>
              <OperacionalKpis data={operacional} isLoading={operLoading} />
              <KPICards data={kpis} meses={meses} isLoading={isLoading} />
              <Grid templateColumns={{ base: '1fr', lg: '1.6fr 1fr' }} gap={6} mb={6}>
                <Stack spacing={6}>
                  <FunilProcessamento
                    importados={kpis?.lancamentos_exportaveis ?? 0}
                    conciliados={kpis?.conciliados_exportaveis ?? 0}
                    exportados={operacional?.exportado_pct.valor ?? 0}
                    aguardandoIdentificacao={operacional?.aguardando_identificacao.valor ?? 0}
                  />
                  <HomologacaoPdf data={resumo?.homologacao} />
                </Stack>
                <EmpresasPanel data={resumo} />
              </Grid>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                <YearDetailPanel meses={meses} ano={ano} />
                <GlassCard intensity="medium" p={6} h="full" minH="280px">
                  <SectionTitle>Status dos meses</SectionTitle>
                  {isLoading ? (
                    <Flex justify="center" py={8}>
                      <Spinner color="#7C3AED" />
                    </Flex>
                  ) : (
                    <Stack spacing={2}>
                      {meses
                        .filter(m => m.total > 0)
                        .map(m => {
                          const pct = m.total > 0 ? (m.conciliados / m.total) * 100 : 0;
                          return (
                            <Flex key={m.mes} align="center" gap={3}>
                              <Text fontSize="xs" color="#64748B" fontWeight="600" w="28px">
                                {String(m.mes).padStart(2, '0')}
                              </Text>
                              <Box flex={1} h="8px" borderRadius="full" bg="rgba(15,23,42,0.06)">
                                <Box
                                  h="full"
                                  borderRadius="full"
                                  w={`${pct}%`}
                                  bg={pct >= 100 ? '#10B981' : '#F59E0B'}
                                />
                              </Box>
                              <Text
                                fontSize="11px"
                                color="#0F172A"
                                fontWeight="700"
                                w="42px"
                                textAlign="right"
                              >
                                {pct.toFixed(0)}%
                              </Text>
                            </Flex>
                          );
                        })}
                      {meses.every(m => m.total === 0) && (
                        <Text fontSize="sm" color="#94A3B8" textAlign="center" py={6}>
                          Sem lançamentos em {ano}.
                        </Text>
                      )}
                    </Stack>
                  )}
                </GlassCard>
              </SimpleGrid>

              <Box mt={6}>
                <FechamentosCalendar ate={ano} />
              </Box>

              <Box mt={6}>
                <VolumeCalendar
                  meses={meses}
                  exportadosSerie={operacional?.exportado_pct.serie ?? []}
                  ano={ano}
                />
              </Box>

              <Box mt={6}>
                <EmpresasTabela />
              </Box>
            </TabPanel>

            {/* ---------- OPERACIONAL ---------- */}
            <TabPanel p={0}>
              {/* Alertas */}
              {nAIdentificar > 0 && (
                <GlassCard intensity="medium" p={4} mb={6}>
                  <Flex
                    as="button"
                    align="center"
                    gap={3}
                    w="full"
                    textAlign="left"
                    onClick={() => navigate(EXTRATOS_PATH)}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      w="40px"
                      h="40px"
                      borderRadius="lg"
                      bg="rgba(245, 158, 11, 0.12)"
                      color="#F59E0B"
                      flexShrink={0}
                    >
                      <Warning size={22} weight="duotone" />
                    </Box>
                    <Box flex={1}>
                      <Text fontSize="sm" fontWeight="700" color="#0F172A">
                        {formatNumber(nAIdentificar)} importação(ões) a identificar
                      </Text>
                      <Text fontSize="xs" color="#64748B">
                        Extratos aguardando vínculo com empresa — resolva pra liberar a conciliação.
                      </Text>
                    </Box>
                    <CaretRight size={16} weight="bold" color="#CBD5E1" />
                  </Flex>
                </GlassCard>
              )}

              {/* Ranking de empresas por pendência */}
              <GlassCard intensity="medium" p={6}>
                <Flex justify="space-between" align="baseline" mb={1}>
                  <SectionTitle>Empresas que precisam de atenção</SectionTitle>
                  {saude.data && (
                    <Text fontSize="11px" color="#94A3B8" fontWeight="600">
                      {formatBRL(saude.data.valor_pendente_total)} pendente
                    </Text>
                  )}
                </Flex>
                {saude.isLoading && (
                  <Flex justify="center" py={8}>
                    <Spinner color="#7C3AED" />
                  </Flex>
                )}
                {!saude.isLoading && empresasCriticas.length === 0 && (
                  <Text fontSize="sm" color="#94A3B8" textAlign="center" py={6}>
                    Nenhuma pendência — tudo conciliado. 🎉
                  </Text>
                )}
                {!saude.isLoading && empresasCriticas.length > 0 && (
                  <Stack spacing={1.5}>
                    {empresasCriticas.map(e => (
                      <Flex
                        key={e.empresa_id}
                        as="button"
                        w="full"
                        textAlign="left"
                        align="center"
                        justify="space-between"
                        gap={3}
                        px={3}
                        py={2.5}
                        borderRadius="lg"
                        bg="rgba(15,23,42,0.02)"
                        _hover={{ bg: 'rgba(124,58,237,0.06)' }}
                        onClick={() => navigate(`${EMPRESAS_PATH}?empresa=${e.empresa_id}`)}
                      >
                        <Box flex={1} minW={0}>
                          <Text fontSize="13px" fontWeight="600" color="#0F172A" isTruncated>
                            {e.razao_social || e.nome || `Empresa ${e.empresa_id}`}
                          </Text>
                          <Text fontSize="11px" color="#64748B">
                            {e.percent_conciliado.toFixed(0)}% conciliado
                            {e.qtd_sem_poc > 0 && ` · ${e.qtd_sem_poc} conta(s) sem POC`}
                          </Text>
                        </Box>
                        <Flex align="center" gap={3} flexShrink={0}>
                          <Box textAlign="right">
                            <Text fontSize="13px" fontWeight="700" color="#F59E0B">
                              {formatNumber(e.pendentes)}
                            </Text>
                            <Text fontSize="10px" color="#94A3B8">
                              {formatBRL(e.valor_pendente)}
                            </Text>
                          </Box>
                          <CaretRight size={14} weight="bold" color="#CBD5E1" />
                        </Flex>
                      </Flex>
                    ))}
                  </Stack>
                )}
                <Flex justify="flex-end" mt={4}>
                  <Text
                    as="button"
                    fontSize="xs"
                    fontWeight="600"
                    color="#7C3AED"
                    onClick={() => navigate(CONCILIAR_PATH)}
                  >
                    Ir para o Conciliar →
                  </Text>
                </Flex>
              </GlassCard>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </GlassPageBg>
  );
}
